// filepath: apps/web/src/renderers/task/TaskForm.tsx
//
// Task completion form -- FR-HT-3 / FR-UI-TASK-1 AC-4, AC-5.
//
// Renders a form for completing a claimed task using the pinned form version.
// The pinned version is fetched via useDefinition(defKey) where defKey uses
// task.form_version (never 'active'). Submit goes to
// /api/tasks/:id/complete via useCompleteTask.
//
// Architecture:
//   useDefinition(defKey)         -- fetches the pinned form definition
//   FieldRegistry (direct render) -- renders fields using the same components
//                                   as FormRenderer
//   useState (field values)       -- managed locally
//   useCompleteTask               -- POST to /api/tasks/:id/complete
//   ConflictResolution modal      -- opened when useCompleteTask fires 409
//
// AC-4: the pinned form version is always fetched via taskFormKey(task).
//   The form_version from the task payload is used -- never 'active'.
//
// AC-5: on 409, useCompleteTask calls resolver.show() and the
//   ConflictResolution modal (mounted here) opens. The task stays
//   'claimed' so the user can retry with the merged payload.

import React, { useState, useCallback } from "react";
import { useDefinition } from "@/definitions/useDefinition";
import {
  fieldRegistry,
  UnknownField,
  type FieldComponentProps,
} from "@/renderers/form/FieldRegistry";
import { SkeletonLayout } from "@/design-system/error-states/SkeletonLayout";
import { FetchError } from "@/design-system/error-states/FetchError";
import { InlineFieldError } from "@/design-system/error-states/InlineFieldError";
import { useCompleteTask } from "@/features/tasks/useCompleteTask";
import { useConflictResolver } from "@/renderers/__shared__/useConflictResolver";
import { ConflictResolution } from "@/design-system/ConflictResolution/ConflictResolution";
import type { HumanTask } from "@/features/tasks/types";
import type { DefinitionKey, FieldMetadata } from "@/definitions/types";
import { taskFormKey } from "@/features/tasks/types";
import { useTranslation } from "@/i18n/useTranslation";
import { getDefinitionFieldIds } from "@/renderers/__shared__/serverShape";
import { useIsMobile, MIN_TOUCH_TARGET_PX } from "@/runtime/useIsMobile";

export interface TaskFormProps {
  /** The claimed human task. Must have status 'claimed'. */
  task: HumanTask;
  /** Called on successful task completion. */
  onCompleted?: () => void;
  /** Called when the user cancels / discards the form. */
  onCancel?: () => void;
}

/** Resolve field iteration order: ui_schema.order wins when present. */
function resolveFieldOrder(
  definition: Record<string, FieldMetadata | string | undefined>,
  uiSchemaOrder: string[] | undefined,
): string[] {
  if (uiSchemaOrder && uiSchemaOrder.length > 0) {
    const ids = getDefinitionFieldIds(definition);
    return uiSchemaOrder.filter((id) => ids.includes(id));
  }
  return getDefinitionFieldIds(definition);
}

/**
 * TaskForm — renders the pinned form for task completion.
 *
 * Fetches the pinned form definition and renders fields using the same
 * FieldRegistry + FieldComponent pattern as FormRenderer, but submits
 * via useCompleteTask to /api/tasks/:id/complete instead of the entity
 * write endpoint.
 *
 * The ConflictResolution modal is mounted here (not inside FormRenderer)
 * so it responds ONLY to conflicts from useCompleteTask, not from any
 * other source.
 */
export function TaskForm({
  task,
  onCompleted,
  onCancel,
}: TaskFormProps): React.ReactElement {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const resolver = useConflictResolver();

  const defKey: DefinitionKey = taskFormKey(task);

  // Fetch the pinned form definition (AC-4: never 'active').
  const { data, status, error, refetch } = useDefinition(defKey);

  // Local form values — submitted to useCompleteTask on form submit.
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [fieldErrors, setFieldErrors] = useState<
    Record<string, { code: string; message: string }>
  >({});

  const { mutate, isPending: isSubmitting } = useCompleteTask({
    onConflict: (err, userRecord) => {
      resolver.show(err, userRecord);
    },
    onSuccess: () => {
      onCompleted?.();
    },
  });

  const handleFieldChange = useCallback((fieldId: string, next: unknown) => {
    setValues((prev) => ({ ...prev, [fieldId]: next }));
    setFieldErrors((prev) => {
      if (!(fieldId in prev)) return prev;
      const nextErrors = { ...prev };
      delete nextErrors[fieldId];
      return nextErrors;
    });
  }, []);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    mutate({
      taskId: task.id,
      rowVersion: task.row_version,
      formData: values,
    });
  }

  function handleCancel(): void {
    onCancel?.();
  }

  function handleConflictResolve(
    action: "reapply" | "refetch" | "discard",
  ): void {
    const result = resolver.resolve(action);

    if (action === "refetch") {
      // Re-fetch the form definition — task stays claimed, user can retry.
      void refetch();
      return;
    }

    if (action === "discard") {
      // User threw away their changes.
      onCancel?.();
      return;
    }

    // action === "reapply"
    if (!result || result.currentRowVersion === null || !result.mergedPayload) {
      onCancel?.();
      return;
    }

    mutate({
      taskId: task.id,
      rowVersion: result.currentRowVersion,
      formData: result.mergedPayload,
    });
  }

  // 5 mandatory error states per react-conventions §1.
  if (status === "loading") return <SkeletonLayout />;
  if (status === "permission-denied") {
    return (
      <FetchError
        message="You do not have permission to view this form."
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }
  if (status === "stale-version") {
    return (
      <FetchError
        message="This form version is no longer available."
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }
  if (status === "rate-limit") {
    return (
      <FetchError
        message="Too many requests. Please wait and try again."
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }
  if (status === "fetch-failure" || error) {
    return (
      <FetchError
        message="We could not load this form. Please try again."
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  if (!data) return <SkeletonLayout />;

  const definition = data.definition;
  const uiSchema = data.ui_schema;
  const order = resolveFieldOrder(definition, uiSchema?.order);

  return (
    <div data-testid="task-form">
      <form
        onSubmit={handleSubmit}
        aria-busy={isSubmitting}
        aria-labelledby="task-form-title"
      >
        <header style={{ marginBottom: "16px" }}>
          <h2
            id="task-form-title"
            data-testid="task-form-title"
            style={{ margin: "0 0 4px 0", fontSize: "16px", fontWeight: 700 }}
          >
            {data.form_id}
          </h2>
          <span
            id="task-form-version"
            data-testid="form-version"
            style={{ color: "#6b7280", fontSize: "12px" }}
          >
            v{data.version}
          </span>
        </header>

        {order.map((fieldId) => {
          const metadata = definition[fieldId];
          if (metadata === undefined || typeof metadata === "string") {
            return null;
          }

          // Fields with fieldType 'hidden' are cosmetic (FR-FORM-4 AC-9b).
          // Skip rendering them as inputs but preserve their value in the form state.
          // INV-2: server has already stripped any truly unauthorized fields.
          if (metadata.fieldType === "hidden") {
            return (
              <input
                key={fieldId}
                type="hidden"
                name={fieldId}
                value={String(values[fieldId] ?? "")}
              />
            );
          }

          const FieldComponent = fieldRegistry.get(metadata.fieldType);
          const fieldErrorEntry = fieldErrors[fieldId];
          const fieldErrorId = `${fieldId}-error`;
          const fieldErrorProp = fieldErrorEntry
            ? {
                id: fieldErrorId,
                message: fieldErrorEntry.message,
                code: fieldErrorEntry.code,
              }
            : null;

          const componentProps: FieldComponentProps = {
            fieldId,
            metadata,
            value: values[fieldId],
            onChange: (v) => handleFieldChange(fieldId, v),
            uiSchema: uiSchema ?? null,
            fieldError: fieldErrorProp,
          };

          return (
            <React.Fragment key={fieldId}>
              {FieldComponent ? (
                <FieldComponent {...componentProps} />
              ) : (
                <UnknownField {...componentProps} />
              )}
              {fieldErrorEntry ? (
                <InlineFieldError
                  id={fieldErrorId}
                  message={fieldErrorEntry.message}
                  code={fieldErrorEntry.code}
                />
              ) : null}
            </React.Fragment>
          );
        })}

        {/* Submit / Cancel actions. */}
        <div
          style={{
            display: isMobile ? "flex" : "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "12px" : "12px",
            marginTop: "24px",
            paddingTop: "16px",
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              flex: isMobile ? undefined : 1,
              width: isMobile ? "100%" : undefined,
              minHeight: `${MIN_TOUCH_TARGET_PX}px`,
              padding: isMobile ? "12px 16px" : "8px 20px",
              borderRadius: "var(--radius-md)",
              border: "none",
              backgroundColor: isSubmitting ? "#93c5fd" : "#3b82f6",
              color: "#ffffff",
              fontSize: isMobile ? "16px" : "14px",
              fontWeight: 500,
              cursor: isSubmitting ? "not-allowed" : "pointer",
            }}
          >
            {isSubmitting
              ? t("task.complete.submitting", "Submitting...")
              : t("task.complete.submit", "Complete Task")}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            style={{
              flex: isMobile ? undefined : 1,
              width: isMobile ? "100%" : undefined,
              minHeight: `${MIN_TOUCH_TARGET_PX}px`,
              padding: isMobile ? "12px 16px" : "8px 20px",
              borderRadius: "var(--radius-md)",
              border: isMobile ? "none" : "1px solid #d1d5db",
              backgroundColor: isMobile ? "#6b7280" : "#ffffff",
              color: "#ffffff",
              fontSize: isMobile ? "16px" : "14px",
              fontWeight: 500,
              cursor: isSubmitting ? "not-allowed" : "pointer",
            }}
          >
            {t("common.cancel", "Cancel")}
          </button>
        </div>
      </form>

      {/* ConflictResolution modal — mounted here so it responds ONLY to
          useCompleteTask 409s (not to any other conflict source).
          Renders nothing when no conflict is active. */}
      <ConflictResolution
        onResolve={handleConflictResolve}
        onDismiss={() => resolver.dismiss()}
      />
    </div>
  );
}
